---
title: "Adding borrowers by hand"
slug: "adding-borrowers-manually"
category: "borrowers"
description: "For new starters, staff borrowers, or schools that don't want to import a full CSV."
tags: ["borrowers", "add", "manual", "new starter", "staff"]
order: 2
---

Most of the time you'll bring pupils into BookBurrow in one go by [importing from your MIS](/help/guides/importing-from-sims-arbor-bromcom). But there are a few cases where adding a borrower by hand is simpler:

- A single new starter mid-year.
- Staff members who want to borrow books themselves.
- Governors, parent volunteers, or anyone else not in your MIS.
- A tiny school where you'd rather type thirty names than wrangle a CSV.

## Adding a borrower

<Steps>
  <Step title="Open the Borrowers tab">
    The person-shaped icon in the sidebar.
  </Step>
  <Step title="Click Add borrower">
    The form opens in a panel on the right.
  </Step>
  <Step title="Fill in the essentials">
    First name, surname, and a category (Pupil or Staff) are the only required fields. Everything else is optional and can be edited later.
  </Step>
  <Step title="Pick a year group and class">
    For pupils, set the year group. You can leave the class blank if your school doesn't use them — it's only used for filtering reports.
  </Step>
  <Step title="Give the borrower a card number">
    Type any number you like, or click **Generate** to have BookBurrow pick the next available one. If you have a barcode scanner and blank labels, you can scan a label to use that barcode as the card number.
  </Step>
  <Step title="Click Save">
    The new borrower appears in the list and is immediately ready to take loans.
  </Step>
</Steps>

<Callout variant="tip" title="Card numbers don't have to match anything">
The card number is just a unique identifier for the pupil — it doesn't have to match their MIS ID, their admission number, or anything else. Pick whatever makes sense for your school. Most schools use sequential numbers starting from 1001.
</Callout>

## Bulk-adding several at once

If you're adding a handful of new starters — say five pupils who've joined mid-term — and you'd rather not open the form five times, use **Borrowers → Bulk add**. You can paste a block of text, one borrower per line, in the format:

```
Smith, Alice, Year 3, 3A
Jones, Bob, Year 3, 3B
Khan, Ahmed, Year 5, 5A
```

BookBurrow parses each line and adds the borrower. This is much faster than the full CSV importer for fewer than ten records.

## Editing or deleting later

You can edit any borrower by clicking their row in the list. Everything is editable — name, year group, class, card number, even their full history.

Deleting is also straightforward: **Borrowers → [pupil row] → Delete**. BookBurrow will ask for confirmation, and if the pupil has active loans it will either refuse to delete them (you'd need to return the books first) or mark their loans as "lost" depending on your settings.

## Staff borrowers

Staff are treated just like pupils in terms of lending and returns, but with a separate category so they don't show up in pupil reports. To add a staff member:

<Steps>
  <Step title="Click Add borrower">
    Same as above.
  </Step>
  <Step title="Set category to Staff">
    This hides them from year-group filters and reports.
  </Step>
  <Step title="Leave year group blank">
    Or set it to a special value like "Staff" if your school prefers.
  </Step>
</Steps>

Staff borrowers do get counted in overall stock and loan summaries, which is usually what you want — knowing the head teacher has borrowed the new Michael Rosen is useful management information.
